import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { MapPin, Mail, Phone, ArrowRight } from 'lucide-react';
import { destinations, companyInfo } from '../data/mock';
import { LanguageSwitcher } from './LanguageSwitcher';

export const Footer = ({ onNavigate }) => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { key: 'destinations', label: t('nav.destinations') },
    { key: 'experiences', label: t('nav.experiences') },
    { key: 'yachts', label: t('nav.yachts') },
    { key: 'tailorMade', label: t('nav.tailorMade') },
    { key: 'about', label: t('nav.about') },
    { key: 'contact', label: t('nav.contact') },
  ];

  return (
    <footer className="bg-stone-900 text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <div className="mb-6">
              <div className="text-2xl font-serif">
                <span className="font-light">Bali</span>
                <span className="font-semibold text-amber-400"> Lux</span>
                <span className="font-light"> Vacances</span>
              </div>
              <div className="text-xs uppercase tracking-[0.2em] text-stone-500 mt-1">
                Luxury Travel Indonesia
              </div>
            </div>
            <p className="text-stone-400 text-sm leading-relaxed mb-6">
              {t('footer.tagline')}
            </p>
            <LanguageSwitcher />
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-medium uppercase tracking-wider text-stone-300 mb-6">
              {t('footer.quickLinks')}
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.key}>
                  <button
                    onClick={() => onNavigate(link.key)}
                    className="text-stone-400 hover:text-amber-400 transition-colors text-sm flex items-center gap-2 group"
                  >
                    <ArrowRight className="w-3 h-3 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Destinations */}
          <div>
            <h4 className="text-sm font-medium uppercase tracking-wider text-stone-300 mb-6">
              {t('footer.destinations')}
            </h4>
            <ul className="space-y-3">
              {destinations.slice(0, 6).map((dest) => (
                <li key={dest.id}>
                  <button
                    onClick={() => onNavigate(`destination-${dest.id}`)}
                    className="text-stone-400 hover:text-amber-400 transition-colors text-sm flex items-center gap-2 group"
                  >
                    <ArrowRight className="w-3 h-3 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {dest.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-sm font-medium uppercase tracking-wider text-stone-300 mb-6">
              {t('footer.contact')}
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-amber-400 mt-0.5" />
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="text-stone-400 hover:text-amber-400 transition-colors text-sm"
                >
                  {companyInfo.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-amber-400 mt-0.5" />
                <span className="text-stone-400 text-sm">
                  {companyInfo.phone}
                </span>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-amber-400 mt-0.5" />
                <span className="text-stone-400 text-sm">
                  {companyInfo.address}
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-stone-500 text-sm">
              © {currentYear} {companyInfo.name}. {t('footer.rights')}
            </div>
            <div className="flex items-center gap-6 text-sm">
              <a href="#" className="text-stone-500 hover:text-amber-400 transition-colors">
                {t('footer.privacy')}
              </a>
              <a href="#" className="text-stone-500 hover:text-amber-400 transition-colors">
                {t('footer.terms')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
